/**
 * Local storage service for device priorities
 * Provides browser-based persistence for device favorites/priorities
 */

export interface DevicePriorityData {
  priorityDeviceIds: string[]
  lastUpdated: string
}

class DevicePrioritiesService {
  /**
   * Get storage key scoped to user
   */
  private getStorageKey(userId?: string): string {
    if (!userId) {
      return 'device_priorities' // fallback for anonymous/legacy usage
    }
    return `device_priorities_${userId}`
  }

  /**
   * Get priority device IDs from localStorage for a specific user
   */
  getPriorityDeviceIds(userId?: string): string[] {
    try {
      const stored = localStorage.getItem(this.getStorageKey(userId))
      if (!stored) return []

      const data: DevicePriorityData = JSON.parse(stored)
      return data.priorityDeviceIds || []
    } catch (error) {
      console.error('Failed to load device priorities from localStorage:', error)
      return []
    }
  }

  /**
   * Save priority device IDs to localStorage for a specific user
   */
  setPriorityDeviceIds(deviceIds: string[], userId?: string): void {
    try {
      const data: DevicePriorityData = {
        priorityDeviceIds: deviceIds,
        lastUpdated: new Date().toISOString(),
      }
      localStorage.setItem(this.getStorageKey(userId), JSON.stringify(data))
    } catch (error) {
      console.error('Failed to save device priorities to localStorage:', error)
    }
  }

  /**
   * Check if a device is in the priority list for a specific user
   */
  isPriorityDevice(deviceId: string, userId?: string): boolean {
    const priorityIds = this.getPriorityDeviceIds(userId)
    return priorityIds.includes(deviceId)
  }

  /**
   * Add a device to the priority list for a specific user
   */
  addPriorityDevice(deviceId: string, userId?: string): string[] {
    const priorityIds = this.getPriorityDeviceIds(userId)

    if (priorityIds.includes(deviceId)) {
      return priorityIds // Already in list
    }

    const updatedIds = [...priorityIds, deviceId]

    // Limit to maximum 10 priority devices
    const limitedIds = updatedIds.length > 10 ? updatedIds.slice(-10) : updatedIds

    this.setPriorityDeviceIds(limitedIds, userId)
    return limitedIds
  }

  /**
   * Remove a device from the priority list for a specific user
   */
  removePriorityDevice(deviceId: string, userId?: string): string[] {
    const priorityIds = this.getPriorityDeviceIds(userId)
    const updatedIds = priorityIds.filter((id) => id !== deviceId)

    this.setPriorityDeviceIds(updatedIds, userId)
    return updatedIds
  }

  /**
   * Toggle device priority status for a specific user
   */
  toggleDevicePriority(deviceId: string, isPriority: boolean, userId?: string): string[] {
    if (isPriority) {
      return this.addPriorityDevice(deviceId, userId)
    } else {
      return this.removePriorityDevice(deviceId, userId)
    }
  }

  /**
   * Reorder priority devices for a specific user
   */
  reorderPriorityDevices(deviceIds: string[], userId?: string): string[] {
    // Validate input (max 10 devices)
    const limitedIds = deviceIds.length > 10 ? deviceIds.slice(0, 10) : deviceIds

    this.setPriorityDeviceIds(limitedIds, userId)
    return limitedIds
  }

  /**
   * Get priority order for a device for a specific user
   */
  getPriorityOrder(deviceId: string, userId?: string): number {
    const priorityIds = this.getPriorityDeviceIds(userId)
    const index = priorityIds.indexOf(deviceId)
    return index >= 0 ? index : -1
  }

  /**
   * Clear all priority devices for a specific user
   */
  clearAllPriorities(userId?: string): void {
    localStorage.removeItem(this.getStorageKey(userId))
  }

  /**
   * Clear priorities for all users (cleanup utility)
   */
  clearAllUserPriorities(): void {
    // Find all device_priorities_* keys
    const keysToRemove: string[] = []
    for (let i = 0; i < localStorage.length; i++) {
      const key = localStorage.key(i)
      if (key && (key === 'device_priorities' || key.startsWith('device_priorities_'))) {
        keysToRemove.push(key)
      }
    }

    // Remove all found keys
    keysToRemove.forEach((key) => localStorage.removeItem(key))
  }

  /**
   * Export priority data for a specific user (for future database sync)
   */
  exportPriorityData(userId?: string): DevicePriorityData | null {
    try {
      const stored = localStorage.getItem(this.getStorageKey(userId))
      return stored ? JSON.parse(stored) : null
    } catch (error) {
      console.error('Failed to export priority data:', error)
      return null
    }
  }

  /**
   * Import priority data for a specific user (for future database sync)
   */
  importPriorityData(data: DevicePriorityData, userId?: string): void {
    try {
      localStorage.setItem(this.getStorageKey(userId), JSON.stringify(data))
    } catch (error) {
      console.error('Failed to import priority data:', error)
    }
  }
}

// Export singleton instance
export const devicePrioritiesService = new DevicePrioritiesService()
